import { isDefined } from "@clipboard-health/util-ts";
import { CircularProgress, Box, Stack, TextField, Button } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import axios from "axios";
import { useState } from "react";
import { type WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import { useUpdateLateCancellationCallNotification } from "@src/appV2/FacilityProfile/api/useUpdateLateCancellationCallNotification";
import { formatUsPhoneNumber } from "@src/appV2/lib";
import { SettingsItem } from "./SettingsItem";
import { SettingsItemTitle } from "./SettingsItemTitle";

interface LateCancellationCallNotificationSettingProps {
  workplace: WorkplaceProfileResponse;
  onPreferencesChanged: () => void;
}

function getDefaultInstructions(workplace: WorkplaceProfileResponse): string {
  const { phone } = workplace.data.attributes;
  if (isDefined(phone) && phone.length > 0) {
    return `Call ${formatUsPhoneNumber(phone)}`;
  }
  return "Call the workplace phone number";
}

export function LateCancellationCallNotificationSetting(
  props: LateCancellationCallNotificationSettingProps
) {
  const { workplace, onPreferencesChanged } = props;

  const existingConfig = workplace.data.attributes.workerAttendanceNotifications;

  const [enabled, setEnabled] = useState(existingConfig?.enabled ?? false);
  const [instructions, setInstructions] = useState(
    existingConfig?.instructions ?? getDefaultInstructions(workplace)
  );
  const [validationError, setValidationError] = useState("");

  const { showSuccessToast } = useToast();

  const { isLoading: isUpdating, mutateAsync: patchLateCancellationCallNotification } =
    useUpdateLateCancellationCallNotification();

  async function updateSetting(params: { enabled: boolean; instructions?: string }) {
    const previousEnabled = enabled;

    setEnabled(params.enabled);

    try {
      await patchLateCancellationCallNotification({
        workplaceId: workplace.data.id,
        workerAttendanceNotifications: params,
      });

      showSuccessToast("Late-cancellation call notification preferences updated successfully");
      onPreferencesChanged();
    } catch (error: unknown) {
      const fallbackMessage =
        "Error updating late-cancellation call notification preferences. Please try again later";
      if (axios.isAxiosError(error) && isDefined(error.response)) {
        const { data } = error.response;
        let message = fallbackMessage;
        if (typeof data === "string") {
          message = data;
        } else if (typeof data?.message === "string") {
          message = data.message;
        }
        showErrorToast(message);
      } else {
        showErrorToast(fallbackMessage);
      }

      setEnabled(previousEnabled);
    }
  }

  function handleSaveInstructions() {
    if (instructions.trim().length === 0) {
      setValidationError("Call instructions are required");
      return;
    }

    setValidationError("");
    updateSetting({ enabled: true, instructions });
  }

  return (
    <Stack position="relative" sx={{ opacity: isUpdating ? 0.5 : 1 }}>
      {isUpdating ? (
        <Box position="absolute" top="calc(50% - 24px)" left="calc(50% - 24px)">
          <CircularProgress size={48} />
        </Box>
      ) : null}
      <SettingsItem
        key="lateCancellationCallNotification"
        isLoading={isUpdating}
        item={{
          key: "lateCancellationCallNotification",
          title: "Late-Cancellation Call Notification",
          subtitle:
            "When enabled, Clipboard will call the specified number per the instructions below if a professional cancels within 8 hours of the shift start time or No Shows.",
          checked: enabled,
          onChange: (checked: boolean) => {
            if (checked) {
              updateSetting({ enabled: true, instructions });
            } else {
              setValidationError("");
              updateSetting({ enabled: false });
            }
          },
        }}
      />
      {enabled && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            paddingY: 3,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <Stack direction="column" spacing={0}>
            <SettingsItemTitle
              title="Late-Cancellation Call Instructions"
              subtitle="Provide call instructions for handling late cancellations and no-shows."
            />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              minWidth: "400px",
            }}
            justifyContent="flex-end"
          >
            <TextField
              value={instructions}
              onChange={(e) => {
                setInstructions(e.target.value);
                if (e.target.value.trim().length > 0) {
                  setValidationError("");
                }
              }}
              disabled={isUpdating}
              size="small"
              multiline
              minRows={2}
              inputProps={{
                "aria-label": "Late-Cancellation Call Instructions",
              }}
              placeholder="Enter call instructions, e.g. call (xxx)-xxx-xxxx, ask for the charge nurse"
              error={validationError.length > 0}
              helperText={validationError.length > 0 ? validationError : undefined}
              sx={{ flex: 1 }}
            />
            <Button
              variant="text"
              color="primary"
              onClick={handleSaveInstructions}
              disabled={isUpdating}
            >
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
